// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import { formatBytes } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import FileInput from "@/UI/inputs/FileInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyUploadFilesDetails = {
    /** Pre-set when the upload is opened from a record's Files tab rather than from the drive. */
    owner?: { model: string; id: string } | null;
    /**
     * What these files are **to** the record, when a record's own form opened this — its gallery,
     * its signed paper. Left out by the drive and by a files tab, and that absence is what marks a
     * file as merely filed under the record rather than part of it.
     */
    slot?: string | null;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * A side form, like every create and update form in this product — but it does not wait for anything.
 *
 * Picking files hands them to the upload tray and closes. The bytes go straight to S3 from there, so
 * the person can carry on working while a long video uploads behind them; the tray is where the
 * progress, the cancel and the retry live.
 */
export default function UploadFilesPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { owner, slot } = (details as TyUploadFilesDetails) ?? {};
    const enqueue = useUploads((state) => state.enqueue);

    const [files, setFiles] = useState<File[]>([]);
    const [name, setName] = useState("");
    const [tags, setTags] = useState("");
    const [formError, setFormError] = useState<string | null>(null);

    const handleSubmit = () => {
        setFormError(null);
        if (!files.length) return setFormError(t.drive.pick);

        enqueue(files, {
            model: owner?.model ?? "",
            id: owner?.id ?? "",
            ...(slot ? { slot } : {}),
            // One name only makes sense for one file; a batch keeps each its own.
            ...(files.length === 1 && name.trim() ? { name: name.trim() } : {}),
            ...(tags.trim()
                ? {
                      tags: tags
                          .split(",")
                          .map((tag) => tag.trim())
                          .filter(Boolean),
                  }
                : {}),
        });
        onClose();
    };

    const total = files.reduce((sum, file) => sum + file.size, 0);

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.drive.uploadTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="side-form__hint">{t.drive.uploadHint}</p>

                <FileInput files={files} onChange={setFiles} />

                {files.length === 1 && <TextInput value={name} onChange={setName} label={t.drive.name} placeholder={t.drive.namePlaceholder} />}
                <TextInput value={tags} onChange={setTags} label={t.drive.tags} placeholder={t.drive.tagsPlaceholder} />

                {files.length > 0 && (
                    <p className="side-form__hint">
                        {t.drive.selected.replace("{count}", String(files.length))} · {formatBytes(total, lang)}
                    </p>
                )}

                {formError && <p className="side-form__error">{formError}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit}>
                    {t.drive.upload}
                </button>
            </footer>
        </section>
    );
}
